/**
 * The bell's wire layer — `GET /me/notifications`, `POST /me/notifications/:id/read`.
 *
 * ===========================================================================
 * WHY THIS EXISTS NOW, WHEN THE BELL WAS DELIBERATELY DEAD BEFORE
 * ===========================================================================
 * `TopBar` shipped the bell DISABLED in Phase 2, with the reason recorded on the
 * control itself: "`notifications` is a Phase 0 table with no read route in this
 * phase's contract, so the bell has nothing to open and no count to show...
 * drawing an unread dot over zero data would be a fabricated number in the UI."
 *
 * That reasoning was right and is now OUT OF DATE. Phase 4 built the pull half —
 * `MeNotificationsController` — and two live wire-ups that write to it
 * (`work.assigned`, and the test-round return). So the honest state changed: the
 * count is now a real number from a real route, and leaving the bell disabled
 * would mean the app knows a developer has been told something and refuses to
 * show them.
 *
 * ===========================================================================
 * NO PERMISSION GATE, ON PURPOSE
 * ===========================================================================
 * The route is guarded by identity alone (`@CurrentUser()` — "your own
 * notifications are yours by identity"), not by any `@RequirePerm`. So this
 * layer applies no permission check either. Adding one here would invent a rule
 * the server does not have, and the first person to notice would be a Developer
 * whose bell was empty for no reason.
 */

import { apiRequest } from '../../lib/api';

/**
 * One row of the feed — `FeedRow`, joined to its event.
 *
 * `verb`, `entity_type` and `entity_id` are the whole payload: the notification
 * table stores no message text, and this client renders a sentence from the verb
 * rather than displaying a raw enum. A notification whose event row is gone
 * arrives with nulls (the join is a LEFT JOIN), which is a real state and is
 * rendered as such rather than crashing a `.replace()`.
 */
export interface NotificationDto {
  id: string;
  read_at: string | null;
  created_at: string;
  verb: string | null;
  entity_type: string | null;
  entity_id: string | null;
  country_id: number | null;
}

export interface NotificationFeedDto {
  data: NotificationDto[];
  /**
   * The server's own count of UNREAD rows — not `data.filter(...).length`.
   *
   * The list is capped at 50 by the service; the count is not. Deriving the
   * badge from the page would under-report the moment somebody has 51 unread,
   * which is exactly when the badge matters most.
   */
  unread: number;
}

export async function fetchNotifications(signal?: AbortSignal): Promise<NotificationFeedDto> {
  return apiRequest<NotificationFeedDto>('/me/notifications', { signal });
}

export async function markNotificationRead(id: string): Promise<{ id: string; read_at: string }> {
  return apiRequest(`/me/notifications/${id}/read`, { method: 'POST' });
}

export const notificationKeys = {
  /**
   * `['notifications']` — matching the prefix the work-pipeline mutations already
   * invalidate. That invalidation was written before this feature existed and
   * was, until now, a no-op against an empty cache; it is what makes the badge
   * move the moment an assignment lands.
   */
  feed: () => ['notifications'] as const,
};
